import {
  useTeleportPropertyTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyTypeStrategy/useTeleportPropertyTypeStrategy'
import {
  DatabasePropertyType,
} from '@toolify/server/src/models/mongoose/DatabasePropertyModel/enum/DatabasePropertyType'
import {
  useFormulaPropertyTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyTypeStrategy/useFormulaPropertyTypeStrategy'
import {
  usePeoplePropertyTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyTypeStrategy/usePeoplePropertyTypeStrategy'
import {
  useListPropertyTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyTypeStrategy/useListPropertyTypeStrategy'
import {
  useStatusPropertyTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyTypeStrategy/useStatusPropertyTypeStrategy'
import {
  useRelationPropertyTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyTypeStrategy/useRelationPropertyTypeStrategy'
import {IDatabasePropertyResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IDatabasePropertyResponseInstance'
import {useReverseRelationPropertyTypeStrategy} from './useReverseRelationPropertyTypeStrategy'
import {useVotePropertyTypeStrategy} from './useVotePropertyTypeStrategy'
import {useWatchersPropertyTypeStrategy} from './useWatchersPropertyTypeStrategy'
import {useDatePropertyTypeStrategy} from './useDatePropertyTypeStrategy'
import {useFilesPropertyTypeStrategy} from './useFilesPropertyTypeStrategy'

import {
  useBooleanPropertyTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyTypeStrategy/useBooleanPropertyTypeStrategy'
import {
  useAutoIdPropertyTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyTypeStrategy/useAutoIdPropertyTypeStrategy'
import {
  useCreatedAtPropertyTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyTypeStrategy/useCreatedAtPropertyTypeStrategy'
import {
  useUpdatedAtPropertyTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyTypeStrategy/useUpdatedAtPropertyTypeStrategy'
import {
  useNumberPropertyTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyTypeStrategy/useNumberPropertyTypeStrategy'
import {
  useLocationPropertyTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyTypeStrategy/useLocationPropertyTypeStrategy'
import {PropertyValueType} from '@toolify/server/src/services/DatabasePropertyService/types/PropertyValueType'
import {DatabaseViewType} from '@toolify/server/src/models/mongoose/DatabaseViewModel/enum/DatabaseViewType'
import {
  IDatabaseItemResponseInstance,
} from '@toolify/server/src/controllers/DatabaseItemController/types/IDatabaseItemResponseInstance'
import {
  useValueTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyValueTypeStrategy/composables/useValueTypeStrategy'
import {RestrictedEditReasonType} from '@toolify/client/src/modules/databases/enum/RestrictedEditReasonType'
import {IGroupGranularityDescriptor} from '@toolify/client/src/modules/databases/utils/groupSortModes'

const propertyTypeStrategies = new Map<DatabasePropertyType, ReturnType<typeof createPropertyTypeStrategy>>()

export function usePropertyTypeStrategy(propertyType: DatabasePropertyType): ReturnType<typeof createPropertyTypeStrategy> {
  let strategy = propertyTypeStrategies.get(propertyType)
  if(!strategy) {
    strategy = createPropertyTypeStrategy(propertyType)
    propertyTypeStrategies.set(propertyType, strategy)
  }
  return strategy
}

function createPropertyTypeStrategy (
  propertyType: DatabasePropertyType,
) {

  const getSourcePropertyId = (property: IDatabasePropertyResponseInstance) => {
    return property.id
  }

  const getValueTypeKey = (property: IDatabasePropertyResponseInstance): PropertyValueType => {
    return property.valueType
  }

  const getRestrictedEditReason = (): RestrictedEditReasonType => {
    return RestrictedEditReasonType.ReadOnly
  }

  const getGroupGranularityDescriptor = (_property: IDatabasePropertyResponseInstance): IGroupGranularityDescriptor | null => {
    return null
  }

  const getFormPayloadPatch = (
    property: IDatabasePropertyResponseInstance,
    strategyValue: unknown,
    databaseItem: IDatabaseItemResponseInstance,
  ): {value: unknown, additionalData: unknown} => {
    return {
      value: strategyValue,
      additionalData: null,
    }
  }

  const teleportPropertyTypeStrategy = useTeleportPropertyTypeStrategy
  const formulaPropertyTypeStrategy = useFormulaPropertyTypeStrategy
  const peoplePropertyTypeStrategy = usePeoplePropertyTypeStrategy
  const listPropertyTypeStrategy = useListPropertyTypeStrategy
  const relationPropertyTypeStrategy = useRelationPropertyTypeStrategy
  const statusPropertyTypeStrategy = useStatusPropertyTypeStrategy
  const reverseRelationPropertyTypeStrategy = useReverseRelationPropertyTypeStrategy
  const booleanPropertyTypeStrategy = useBooleanPropertyTypeStrategy
  const votePropertyTypeStrategy = useVotePropertyTypeStrategy
  const watchersPropertyTypeStrategy = useWatchersPropertyTypeStrategy
  const datePropertyTypeStrategy = useDatePropertyTypeStrategy
  const autoIdPropertyTypeStrategy = useAutoIdPropertyTypeStrategy
  const createdAtPropertyTypeStrategy = useCreatedAtPropertyTypeStrategy
  const updatedAtPropertyTypeStrategy = useUpdatedAtPropertyTypeStrategy
  const numberPropertyTypeStrategy = useNumberPropertyTypeStrategy
  const locationPropertyTypeStrategy = useLocationPropertyTypeStrategy
  const filesPropertyTypeStrategy = useFilesPropertyTypeStrategy

  const strategyMap = {
    [DatabasePropertyType.Teleport]: teleportPropertyTypeStrategy,
    [DatabasePropertyType.Formula]: formulaPropertyTypeStrategy,
    [DatabasePropertyType.People]: peoplePropertyTypeStrategy,
    [DatabasePropertyType.Dropdown]: listPropertyTypeStrategy,
    [DatabasePropertyType.Relation]: relationPropertyTypeStrategy,
    [DatabasePropertyType.Status]: statusPropertyTypeStrategy,
    [DatabasePropertyType.ReverseRelation]: reverseRelationPropertyTypeStrategy,
    [DatabasePropertyType.Boolean]: booleanPropertyTypeStrategy,
    [DatabasePropertyType.Rating]: booleanPropertyTypeStrategy,
    [DatabasePropertyType.Button]: booleanPropertyTypeStrategy,
    [DatabasePropertyType.Vote]: votePropertyTypeStrategy,
    [DatabasePropertyType.Watchers]: watchersPropertyTypeStrategy,
    [DatabasePropertyType.Date]: datePropertyTypeStrategy,
    [DatabasePropertyType.AutoId]: autoIdPropertyTypeStrategy,
    [DatabasePropertyType.CreatedAt]: createdAtPropertyTypeStrategy,
    [DatabasePropertyType.UpdatedAt]: updatedAtPropertyTypeStrategy,
    [DatabasePropertyType.Number]: numberPropertyTypeStrategy,
    [DatabasePropertyType.Location]: locationPropertyTypeStrategy,
    [DatabasePropertyType.Files]: filesPropertyTypeStrategy,
  }

  const canGroupByView = (viewType: DatabaseViewType, property: IDatabasePropertyResponseInstance): boolean => {
    if (property.type === DatabasePropertyType.Formula) {
      return true
    }
    return useValueTypeStrategy(property.valueType).canGroupByView(viewType, property)
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if(!strategyMap[propertyType]) {
    return {
      getSourcePropertyId,
      getFormItemValueTypeKey: getValueTypeKey,
      getTableValueTypeKey: getValueTypeKey,
      getFormPayloadPatch,
      getRestrictedEditReason,
      hasRenderComponent: true,
      canGroupByView,
      getGroupGranularityDescriptor,
    }
  }
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const strategy = strategyMap[propertyType]()
  
  const hasRenderComponent = strategy.hasRenderComponent ? strategy.hasRenderComponent() : true

  return {
    getIsActive: strategy.getIsActive,
    getGroupFilter: strategy.getGroupFilter,
    getSourcePropertyId: strategy.getSourcePropertyId || getSourcePropertyId,
    getFormItemValueTypeKey: strategy.getFormItemValueTypeKey || getValueTypeKey,
    getTableValueTypeKey: strategy.getTableValueTypeKey || getValueTypeKey,
    getFormPayloadPatch: strategy.getFormPayloadPatch || getFormPayloadPatch,
    getRestrictedEditReason: strategy.getRestrictedEditReason || getRestrictedEditReason,
    hasRenderComponent,
    canGroupByView,
    getGroupGranularityDescriptor: strategy.getGroupGranularityDescriptor || getGroupGranularityDescriptor,
  }
}
